import { faGamepadAlt, faSquare, faTrash } from '@fortawesome/pro-solid-svg-icons';
import { faGamepadAlt as faGamepadOutline } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useContext, useEffect, useState } from 'react';
import { PlaycastContext } from '../PlaycastWrapper';
import { generators } from '@playcastdotio/Messaging';
import { getIpcMessagingInterface } from '../../api/messaging/protocols';
import { PlaycastGamepadId } from '@playcastdotio/CoreTypes';

type GamepadType = 'physical' | 'virtual' | 'empty';

type GamepadDisplayType = {
  gamepadId: PlaycastGamepadId;
  gamepadType: GamepadType;
};

export const Gamepads = () => {
  const context = useContext(PlaycastContext);
  const ipcMessaging = getIpcMessagingInterface();
  const [display, setDisplay] = useState<GamepadDisplayType[]>([]);

  const classes = {
    physical: 'text-orange-400',
    virtual: 'text-blue-400',
    none: 'text-gray-400',
  };

  useEffect(() => {
    const maxId = Math.max(...context.stateStores.hostGamepadState.gamepads.map((gamepad) => gamepad.gamepadId), 3);
    const gamepads: GamepadDisplayType[] = [];
    for (let i = 0; i <= maxId; i++) {
      const gamepad = context.stateStores.hostGamepadState.gamepads.find((gamepad) => gamepad.gamepadId === i);
      if (gamepad) {
        gamepads.push({
          gamepadId: gamepad.gamepadId,
          gamepadType: (gamepad.isPhysical ? 'physical' : 'virtual') as GamepadType,
        });
      } else {
        gamepads.push({
          gamepadId: i as PlaycastGamepadId,
          gamepadType: 'empty',
        });
      }
    }
    console.log('[Gamepads]: calculated display', gamepads);
    setDisplay(gamepads);
  }, [context.stateStores.hostGamepadState.gamepads]);

  const addVirtualGamepad = (gamepadId: PlaycastGamepadId) => {
    const message = generators.gamepad.initialize(context.stateStores.authState.hostAuthId, gamepadId);
    ipcMessaging.send(message);
    const getStatus = generators.gamepad.getStatus(context.stateStores.authState.hostAuthId);
    ipcMessaging.send(getStatus);
    console.log('[Gamepads] addVirtualGamepad', message);
  };

  const removeVirtualGamepad = (gamepadId: PlaycastGamepadId) => {
    const message = generators.gamepad.remove(context.stateStores.authState.hostAuthId, gamepadId);
    ipcMessaging.send(message);
    const getStatus = generators.gamepad.getStatus(context.stateStores.authState.hostAuthId);
    ipcMessaging.send(getStatus);
    console.log('[Gamepads] removeVirtualGamepad', message);
  };

  return (
    <div className='flex flex-col gap-4'>
      <div className='text-xl font-bold'>Gamepads</div>
      <div className='flex flex-row gap-4'>
        <div className=''>
          <FontAwesomeIcon icon={faSquare} className={classes.physical} /> Physical
        </div>
        <div className=''>
          <FontAwesomeIcon icon={faSquare} className={classes.virtual} /> Virtual
        </div>
        <div className=''>
          <FontAwesomeIcon icon={faSquare} className={classes.none} /> None
        </div>
      </div>
      <div className='flex flex-row gap-8 pl-3'>
        {display.map((gamepad) => {
          return (
            <GamepadDisplay
              gamepadId={gamepad.gamepadId}
              gamepadType={gamepad.gamepadType}
              removeVirtualGamepad={removeVirtualGamepad}
              addVirtualGamepad={addVirtualGamepad}
            />
          );
        })}
      </div>
      <div className='text-xl font-bold'>Player Assignments</div>
      <div className='whitespace-pre font-mono'>{JSON.stringify(context.stateStores.gamepadState.playerMappings, null, 2)}</div>
    </div>
  );
};

export const HostGamepadRaw = () => {
  const context = useContext(PlaycastContext);

  return (
    <div className='flex flex-col gap-4'>
      <div className='text-xl font-bold'>Host Gamepad Raw Data</div>
      <div className='whitespace-pre font-mono'>{JSON.stringify(context.stateStores.hostGamepadState.raw, null, 2)}</div>
    </div>
  );
};

type GamepadDisplayProps = GamepadDisplayType & {
  removeVirtualGamepad: (gamepadId: PlaycastGamepadId) => void;
  addVirtualGamepad: (gamepadId: PlaycastGamepadId) => void;
};

const GamepadDisplay = (props: GamepadDisplayProps) => {
  const classes = {
    physical: 'text-orange-400',
    virtual: 'text-blue-400',
    empty: 'text-gray-400',
    clickable: 'cursor-pointer',
  };

  return (
    <div className={['relative flex flex-col items-center', props.gamepadType === 'empty' ? classes.clickable : ''].join(' ')}>
      <div onClick={props.gamepadType === 'empty' ? () => props.addVirtualGamepad(props.gamepadId) : undefined}>
        <FontAwesomeIcon
          icon={props.gamepadType === 'empty' ? faGamepadOutline : faGamepadAlt}
          className={['text-5xl', classes[props.gamepadType]].join(' ')}
        />
        <div className='absolute -left-3 top-0 aspect-square w-[1.5rem] rounded-full bg-yellow-400 p-1 text-center text-xs font-bold text-black'>
          {props.gamepadId + 1}
        </div>
      </div>
      {props.gamepadType === 'virtual' && (
        <div className='cursor-pointer absolute -right-2 bottom-0 aspect-square w-[1.5rem] rounded-full bg-red-500 p-1 text-center text-xs font-bold text-black' onClick={() => props.removeVirtualGamepad(props.gamepadId)}>
          <FontAwesomeIcon icon={faTrash} />
        </div>
      )}
    </div>
  );
};
